import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Bot, 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Building2,
  ChevronLeft,
  ChevronRight,
  Send,
  Paperclip,
  RefreshCw
} from 'lucide-react';

const AIAgent = () => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  
  // New ticket form state
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'technical',
    assignedTo: '',
    tags: ''
  });

  // Mock data for tickets
  const [tickets] = useState([
    {
      id: 'TICKET-001',
      title: 'Verification Application Processing Delay',
      description: 'Multiple verification applications are stuck in pending review status for over 48 hours',
      status: 'open',
      priority: 'high',
      category: 'technical',
      createdBy: 'Ahmad Rahman (Custodian)',
      assignedTo: 'System Admin',
      createdDate: '2024-01-15T10:30:00Z',
      lastUpdate: '2024-01-15T14:20:00Z',
      tags: ['kyc', 'processing', 'delay'],
      messages: [
        {
          id: 1,
          sender: 'Ahmad Rahman',
          role: 'custodian',
          message: 'I notice that several verification applications submitted yesterday are still showing as pending review. This is blocking our investor onboarding process.',
          timestamp: '2024-01-15T10:30:00Z'
        },
        {
          id: 2,
          sender: 'AI Agent',
          role: 'ai',
          message: 'I understand you\'re experiencing delays with verification application processing. Let me analyze the current status and identify potential bottlenecks in the system.',
          timestamp: '2024-01-15T10:32:00Z'
        },
        {
          id: 3,
          sender: 'System Admin',
          role: 'admin',
          message: 'I\'ve identified the issue - there was a database connection timeout affecting the AML screening service. The issue has been resolved and processing has resumed.',
          timestamp: '2024-01-15T14:20:00Z'
        }
      ]
    },
    {
      id: 'TICKET-002',
      title: 'Wallet Generation API Error',
      description: 'Error 500 when trying to generate new custodial wallets for approved investors',
      status: 'in_progress',
      priority: 'critical',
      category: 'technical',
      createdBy: 'Jennifer Wong (Custodian)',
      assignedTo: 'DevOps Team',
      createdDate: '2024-01-14T16:45:00Z',
      lastUpdate: '2024-01-15T09:15:00Z',
      tags: ['wallet', 'api', 'error'],
      messages: [
        {
          id: 1,
          sender: 'Jennifer Wong',
          role: 'custodian',
          message: 'Unable to create new wallets for 3 approved investors. Getting 500 error when clicking "Generate Wallet Address".',
          timestamp: '2024-01-14T16:45:00Z'
        },
        {
          id: 2,
          sender: 'AI Agent',
          role: 'ai',
          message: 'I\'ve detected multiple wallet generation failures in the logs. This appears to be related to the blockchain node connectivity. I\'m escalating this to the DevOps team for immediate attention.',
          timestamp: '2024-01-14T16:47:00Z'
        }
      ]
    },
    {
      id: 'TICKET-003',
      title: 'User Permission Request - Custodian Access',
      description: 'Request for additional wallet management permissions for new custodian team member',
      status: 'resolved',
      priority: 'medium',
      category: 'access',
      createdBy: 'Sarah Johnson (Custodian)',
      assignedTo: 'Security Team',
      createdDate: '2024-01-12T11:20:00Z',
      lastUpdate: '2024-01-13T15:30:00Z',
      tags: ['permissions', 'legal', 'access'],
      messages: [
        {
          id: 1,
          sender: 'Sarah Johnson',
          role: 'legal',
          message: 'New team member needs access to wallet management module. Please grant appropriate permissions.',
          timestamp: '2024-01-12T11:20:00Z'
        },
        {
          id: 2,
          sender: 'AI Agent',
          role: 'ai',
          message: 'I\'ve reviewed the access request and prepared the permission template for custodian role. Forwarding to security team for approval.',
          timestamp: '2024-01-12T11:25:00Z'
        }
      ]
    },
    {
      id: 'TICKET-004',
      title: 'Payment Processing Timeout',
      description: 'Payment processing taking longer than usual for coupon distributions',
      status: 'open',
      priority: 'medium',
      category: 'process',
      createdBy: 'Michael Chen (Facility Agent)',
      assignedTo: 'Integration Team',
      createdDate: '2024-01-15T08:15:00Z',
      lastUpdate: '2024-01-15T08:15:00Z',
      tags: ['payment', 'facility_agent', 'timeout'],
      messages: [
        {
          id: 1,
          sender: 'Michael Chen',
          role: 'facility_agent',
          message: 'Payment processing is taking longer than usual (>5 minutes) for coupon distribution requests. This is delaying our payment schedules.',
          timestamp: '2024-01-15T08:15:00Z'
        }
      ]
    }
  ]);

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getStatusBadge = (status) => {
    const statusClasses = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority) => {
    const priorityClasses = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return priorityClasses[priority] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <Clock className="h-4 w-4" />;
      case 'in_progress':
        return <RefreshCw className="h-4 w-4" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4" />;
      case 'closed':
        return <XCircle className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCreateTicket = () => {
    console.log('Creating new ticket:', newTicket);
    setShowCreateModal(false);
    setNewTicket({
      title: '',
      description: '',
      priority: 'medium',
      category: 'technical',
      assignedTo: '',
      tags: ''
    });
    alert('Ticket created successfully! The AI Agent will analyze and route your request.');
  };

  const handleSendMessage = () => {
    if (!chatMessage.trim()) return;
    
    console.log('Sending message:', chatMessage);
    // Simulate AI response
    setTimeout(() => {
      alert('AI Agent: Thank you for your message. I\'m analyzing your request and will provide assistance shortly.');
    }, 1000);
    setChatMessage('');
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setActiveView('ticket-details');
  };

  const ticketStats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    critical: tickets.filter(t => t.priority === 'critical').length
  };

  // Page access is controlled by route permissions - no action-level check needed
  if (false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              {activeView === 'ticket-details' && (
                <button
                  onClick={() => setActiveView('dashboard')}
                  className="text-gray-600 hover:text-gray-800 p-1"
                  title="Back to Dashboard"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {activeView === 'dashboard' ? 'AI Support Agent' : 
                   activeView === 'ticket-details' ? `Ticket ${selectedTicket?.id}` : 
                   'AI Support Agent'}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {activeView === 'dashboard' ? 'Manage AI-powered customer support and ticket resolution' : 
                   activeView === 'ticket-details' ? 'View and manage support ticket details' : 
                   'AI Support Agent'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {activeView === 'dashboard' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Raise Ticket
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeView === 'dashboard' ? (
          <>
        

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6 mb-8">
              <div className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 rounded-lg bg-primary-100">
                      <MessageSquare className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="ml-4 min-w-0 flex-1">
                      <p className="text-sm font-medium text-secondary-600 truncate">
                        Total Tickets
                      </p>
                      <p className="text-2xl font-bold">
                        {ticketStats.total}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 rounded-lg bg-info-100">
                      <Clock className="w-6 h-6 text-info-600" />
                    </div>
                    <div className="ml-4 min-w-0 flex-1">
                      <p className="text-sm font-medium text-secondary-600 truncate">
                        Open
                      </p>
                      <p className="text-2xl font-bold">
                        {ticketStats.open}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 rounded-lg bg-warning-100">
                      <RefreshCw className="w-6 h-6 text-warning-600" />
                    </div>
                    <div className="ml-4 min-w-0 flex-1">
                      <p className="text-sm font-medium text-secondary-600 truncate">
                        In Progress
                      </p>
                      <p className="text-2xl font-bold">
                        {ticketStats.inProgress}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 rounded-lg bg-success-100">
                      <CheckCircle className="w-6 h-6 text-success-600" />
                    </div>
                    <div className="ml-4 min-w-0 flex-1">
                      <p className="text-sm font-medium text-secondary-600 truncate">
                        Resolved
                      </p>
                      <p className="text-2xl font-bold">
                        {ticketStats.resolved}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 rounded-lg bg-error-100">
                      <AlertTriangle className="w-6 h-6 text-error-600" />
                    </div>
                    <div className="ml-4 min-w-0 flex-1">
                      <p className="text-sm font-medium text-secondary-600 truncate">
                        Critical
                      </p>
                      <p className="text-2xl font-bold">
                        {ticketStats.critical}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
              {/* Search Card - 60% width (3 out of 5 columns) */}
              <div className="card lg:col-span-3">
                <div className="card-body">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-gray w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search by title or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-input pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* Status Filter Card - 20% width (1 out of 5 columns) */}
              <div className="card lg:col-span-1">
                <div className="card-body">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="form-input"
                  >
                    <option value="all">All Status</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              {/* Priority Filter Card - 20% width (1 out of 5 columns) */}
              <div className="card lg:col-span-1">
                <div className="card-body">
                  <select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                    className="form-input"
                  >
                    <option value="all">All Priorities</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Tickets Table */}
            <div className="bg-white border border-light-gray rounded-lg overflow-hidden overflow-x-auto">
            <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Tickets</h3>
          </div>
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Ticket & Priority
                    </th>
                    <th className="table-header-cell">
                      Status
                    </th>
                    <th className="table-header-cell">
                      Created By
                    </th>
                    <th className="table-header-cell">
                      Assigned To
                    </th>
                    <th className="table-header-cell">
                      Last Update
                    </th>
                    <th className="table-header-cell">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">{ticket.id}</span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(ticket.priority)}`}>
                              {ticket.priority}
                            </span>
                          </div>
                          <div className="text-sm text-gray-900 mt-1">{ticket.title}</div>
                          <div className="text-xs text-gray-500 mt-1">{ticket.category}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(ticket.status)}`}>
                          {getStatusIcon(ticket.status)}
                          <span className="ml-1">{ticket.status.replace('_', ' ')}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ticket.createdBy}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ticket.assignedTo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(ticket.lastUpdate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewTicket(ticket)}
                          className="text-primary-600 hover:text-primary-700 p-1"
                          title="View Ticket"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredTickets.length === 0 && (
                <div className="text-center py-12">
                  <Bot className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
                  <p className="text-gray-600">
                    {searchTerm || filterStatus !== 'all' || filterPriority !== 'all'
                      ? 'Try adjusting your search or filter criteria.'
                      : 'Create your first support ticket to get started.'}
                  </p>
                </div>
              )}
            </div>

            {/* Quick AI Chat */}
            <div className="mt-8 bg-white border border-light-gray rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Bot className="h-5 w-5 mr-2 text-blue-600" />
                Quick AI Assistant
              </h3>
              <div className="flex space-x-4">
                <input
                  type="text"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  placeholder="Ask the AI Agent for help..."
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                />
                <button
                  onClick={handleSendMessage}
                  className="btn-primary"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Ticket Details View */
          selectedTicket && (
            <div className="space-y-6">
              {/* Ticket Header */}
              <div className="bg-white border border-light-gray rounded-lg p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-3">
                      <h1 className="text-2xl font-bold text-gray-900">{selectedTicket.id}</h1>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(selectedTicket.status)}`}>
                        {getStatusIcon(selectedTicket.status)}
                        <span className="ml-1">{selectedTicket.status.replace('_', ' ')}</span>
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(selectedTicket.priority)}`}>
                        {selectedTicket.priority}
                      </span>
                    </div>
                    <h2 className="text-xl text-gray-700 mt-2">{selectedTicket.title}</h2>
                    <p className="text-gray-600 mt-2">{selectedTicket.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Created By</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedTicket.createdBy}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Assigned To</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedTicket.assignedTo}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Created</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedTicket.createdDate)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Last Update</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedTicket.lastUpdate)}</p>
                  </div>
                </div>
                
                {selectedTicket.tags && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-500 mb-2">Tags</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedTicket.tags.map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className="bg-white border border-light-gray rounded-lg">
                <div className="p-6 pb-0">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Conversation</h3>
                </div>
                <div className="border-b border-light-gray"></div>
                <div className="p-6 space-y-4">
                  {selectedTicket.messages.map((message) => (
                    <div key={message.id} className={`flex ${message.role === 'ai' ? 'justify-start' : 'justify-start'}`}>
                      <div className={`max-w-3xl ${
                        message.role === 'ai' ? 'bg-blue-50 border-l-4 border-blue-400' : 'bg-gray-50'
                      } rounded-lg p-4`}>
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="flex items-center">
                            {message.role === 'ai' ? (
                              <Bot className="h-4 w-4 text-blue-600" />
                            ) : message.role === 'admin' ? (
                              <User className="h-4 w-4 text-purple-600" />
                            ) : (
                              <User className="h-4 w-4 text-gray-600" />
                            )}
                            <span className="ml-1 text-sm font-medium text-gray-900">{message.sender}</span>
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(message.timestamp)}</span>
                        </div>
                        <p className="text-sm text-gray-700">{message.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Reply */}
                <div className="mt-6 border-t border-gray-200 pt-6 px-6 py-6">
                  <div className="flex space-x-4">
                    <input
                      type="text"
                      placeholder="Type your reply..."
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                    <button className="btn-primary">
                      <Send className="h-4 w-4 mr-2" />
                      Reply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full mx-4 shadow-2xl">
            <div className="p-6 pb-0">
              <div className="flex items-center mb-4">
                <Plus className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">
                  Raise Support Ticket
                </h3>
              </div>
            </div>
            <div className="border-b border-light-gray"></div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Brief description of the issue"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                  rows={4}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Detailed description of the issue..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="technical">Technical Issue</option>
                    <option value="access">Access Request</option>
                    <option value="process">Process Issue</option>
                    <option value="feature">Feature Request</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={newTicket.tags}
                  onChange={(e) => setNewTicket({...newTicket, tags: e.target.value})}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="kyc, wallet, api"
                />
              </div>
            </div>
            
            <div className="px-6 pb-6">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTicket}
                  className="btn-primary"
                >
                  Create Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAgent; 